import { DollarSign, MapPin, Phone, Mail, Clock, Shield, Users, CheckCircle, Star, Award, CreditCard, Zap, Building, FileText, Calculator, TrendingUp, BookOpen, HelpCircle, Car } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const TitleLoans = () => {
  const titleLoanFeatures = [
    {
      icon: <Car className="w-8 h-8 text-red-600" />,
      title: "Use Your Vehicle",
      description: "Keep driving your car while using it as collateral"
    },
    {
      icon: <DollarSign className="w-8 h-8 text-green-600" />,
      title: "Higher Loan Amounts",
      description: "Borrow more based on your vehicle's value"
    },
    {
      icon: <Zap className="w-8 h-8 text-blue-600" />,
      title: "Quick Approval",
      description: "Fast approval process with minimal paperwork"
    },
    {
      icon: <Shield className="w-8 h-8 text-purple-600" />,
      title: "Keep Your Car",
      description: "Continue using your vehicle during the loan term"
    }
  ];

  const requirements = [
    "Own your vehicle outright (no liens)",
    "Valid government-issued ID",
    "Proof of income or employment",
    "Vehicle registration and insurance",
    "Clear vehicle title in your name",
    "Vehicle inspection may be required"
  ];

  const vehicleTypes = [
    {
      type: "Cars",
      description: "Sedans, coupes, hatchbacks, and convertibles"
    },
    {
      type: "Trucks",
      description: "Pickup trucks, work trucks, and commercial vehicles"
    },
    {
      type: "SUVs",
      description: "Sport utility vehicles and crossovers"
    },
    {
      type: "Motorcycles",
      description: "Motorcycles, scooters, and recreational vehicles"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-100">
      {/* Header */}
      <header className="bg-white shadow-lg">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-4">
              <img 
                src="https://quickloanpro.com/storage/2023/11/Payday-Loans-Louisiana-Logo.png" 
                alt="QuickLoanPro Logo"
                className="w-16 h-16 object-contain"
              />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">QuickLoanPro</h1>
                <p className="text-gray-600">Your Trusted Financial Partner</p>
              </div>
            </Link>
            <div className="hidden md:flex items-center gap-4">
              <Phone className="w-5 h-5 text-green-600" />
              <span className="text-gray-700">+1 (504) 438-1753</span>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-red-600 text-white py-3">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-6">
            <Link to="/" className="hover:text-red-200 transition-colors">Home</Link>
            <Link to="/payday-loans" className="hover:text-red-200 transition-colors">Payday Loans</Link>
            <Link to="/personal-loans" className="hover:text-red-200 transition-colors">Personal Loans</Link>
            <Link to="/installment-loans" className="hover:text-red-200 transition-colors">Installment Loans</Link>
            <Link to="/cash-advances" className="hover:text-red-200 transition-colors">Cash Advances</Link>
            <Link to="/title-loans" className="font-semibold">Title Loans</Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-r from-red-600 to-orange-700 text-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Title Loans Louisiana
              </h2>
              <p className="text-xl md:text-2xl mb-8 opacity-90">
                Use your vehicle's title to get the cash you need. Keep driving while you pay. Higher loan amounts available from QuickLoanPro.
              </p>
              <div className="flex flex-wrap gap-4 mb-8">
                <Badge className="bg-white/20 text-white text-lg px-4 py-2">
                  <Car className="w-5 h-5 mr-2" />
                  Keep Your Car
                </Badge>
                <Badge className="bg-white/20 text-white text-lg px-4 py-2">
                  <DollarSign className="w-5 h-5 mr-2" />
                  Higher Amounts
                </Badge>
                <Badge className="bg-white/20 text-white text-lg px-4 py-2">
                  <Zap className="w-5 h-5 mr-2" />
                  Quick Approval
                </Badge>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold text-xl px-8 py-4"
                  onClick={() => window.open('https://quickloanpro.com/', '_blank')}
                >
                  Apply for Title Loan
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-white text-white hover:bg-white hover:text-red-600 font-bold text-xl px-8 py-4"
                >
                  Get Quote
                </Button>
              </div>
            </div>
            <div className="hidden md:block">
              <img 
                src="/images/payday_loans_1.png" 
                alt="QuickLoanPro Title Loans Louisiana"
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">
            Title Loan Features
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {titleLoanFeatures.map((feature, index) => (
              <Card key={index} className="text-center hover:shadow-xl transition-shadow duration-300 border-2 border-red-200 bg-red-50">
                <CardContent className="p-6">
                  <div className="flex justify-center mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Vehicle Types */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">
            Accepted Vehicle Types
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {vehicleTypes.map((vehicle, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <Car className="w-12 h-12 text-red-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-3">{vehicle.type}</h3>
                  <p className="text-gray-600 text-sm">{vehicle.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="py-16 bg-gradient-to-r from-red-50 to-orange-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6 text-gray-900">
                Title Loan Requirements
              </h2>
              <p className="text-gray-700 mb-6">
                Getting a title loan from QuickLoanPro is straightforward. Here's what you need to qualify for a title loan using your vehicle:
              </p>
              <div className="space-y-3">
                {requirements.map((requirement, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-red-600" />
                    <span className="text-gray-700">{requirement}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8">
                <Button 
                  size="lg" 
                  className="bg-red-600 hover:bg-red-700 text-white font-bold px-8 py-4"
                  onClick={() => window.open('https://quickloanpro.com/', '_blank')}
                >
                  Check If You Qualify
                </Button>
              </div>
            </div>
            <div>
              <img 
                src="/images/payday_loans_2.jpeg" 
                alt="Title Loan Requirements"
                className="rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">
            How Title Loans Work
          </h2>
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              <Card className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-red-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                    1
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Apply Online</h3>
                  <p className="text-gray-600">Complete our application with your vehicle and personal information</p>
                </CardContent>
              </Card>
              
              <Card className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-red-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                    2
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Vehicle Inspection</h3>
                  <p className="text-gray-600">We assess your vehicle's value to determine your loan amount</p>
                </CardContent>
              </Card>
              
              <Card className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-red-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                    3
                  </div>
                  <h3 className="text-xl font-semibold mb-3">Get Your Cash</h3>
                  <p className="text-gray-600">Receive your funds and keep driving your vehicle</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Internal Links Section */}
      <section className="py-16 bg-gradient-to-r from-red-50 to-orange-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">
            Explore Our Other Loan Options
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <DollarSign className="w-8 h-8 text-green-600 mx-auto mb-3" />
                <h3 className="font-semibold mb-2">Payday Loans</h3>
                <p className="text-sm text-gray-600 mb-4">Quick cash until payday</p>
                <Link to="/payday-loans">
                  <Button variant="outline" size="sm">Learn More</Button>
                </Link>
              </CardContent>
            </Card>
            
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <CreditCard className="w-8 h-8 text-blue-600 mx-auto mb-3" />
                <h3 className="font-semibold mb-2">Personal Loans</h3>
                <p className="text-sm text-gray-600 mb-4">Flexible personal financing</p>
                <Link to="/personal-loans">
                  <Button variant="outline" size="sm">Learn More</Button>
                </Link>
              </CardContent>
            </Card>
            
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <Zap className="w-8 h-8 text-orange-600 mx-auto mb-3" />
                <h3 className="font-semibold mb-2">Cash Advances</h3>
                <p className="text-sm text-gray-600 mb-4">Immediate cash access</p>
                <Link to="/cash-advances">
                  <Button variant="outline" size="sm">Learn More</Button>
                </Link>
              </CardContent>
            </Card>
            
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <HelpCircle className="w-8 h-8 text-red-600 mx-auto mb-3" />
                <h3 className="font-semibold mb-2">FAQ</h3>
                <p className="text-sm text-gray-600 mb-4">Get answers</p>
                <Link to="/faq">
                  <Button variant="outline" size="sm">View FAQ</Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-red-600 to-orange-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">
            Ready for Your Title Loan?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Use your vehicle's value to get the cash you need. Keep driving while you pay.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold text-xl px-8 py-4"
              onClick={() => window.open('https://quickloanpro.com/', '_blank')}
            >
              Apply for Title Loan
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-white text-white hover:bg-white hover:text-red-600 font-bold text-xl px-8 py-4"
            >
              Call +1 (504) 438-1753
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-xl font-semibold mb-4">QuickLoanPro</h3>
              <p className="text-gray-400 mb-4">
                Your trusted partner for title loans and vehicle-based financing across Louisiana.
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <span>+1 (504) 438-1753</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <span>info@quickloanpro.com</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  <span>4901 Bloomfield St, New Orleans, LA 70121</span>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Loan Services</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/payday-loans" className="hover:text-white">Payday Loans</Link></li>
                <li><Link to="/personal-loans" className="hover:text-white">Personal Loans</Link></li>
                <li><Link to="/installment-loans" className="hover:text-white">Installment Loans</Link></li>
                <li><Link to="/cash-advances" className="hover:text-white">Cash Advances</Link></li>
                <li><Link to="/title-loans" className="hover:text-white">Title Loans</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Resources</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/how-it-works" className="hover:text-white">How It Works</Link></li>
                <li><Link to="/faq" className="hover:text-white">FAQ</Link></li>
                <li><Link to="/responsible-lending" className="hover:text-white">Responsible Lending</Link></li>
                <li><Link to="/contact" className="hover:text-white">Contact Us</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/about" className="hover:text-white">About Us</Link></li>
                <li><Link to="/privacy" className="hover:text-white">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 QuickLoanPro. All rights reserved.</p>
            <p className="mt-2">Licensed financial service provider committed to responsible lending practices.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TitleLoans;